'use client';

import { getSessionToken } from '@descope/react-sdk';

/**
 * Get Descope session token using Descope SDK with fallback to DS cookie
 * The SDK automatically handles storage based on configuration:
 * - sessionTokenViaCookie: true -> stores in cookie (accessible via JS)
 * - persistTokens: true -> stores in localStorage
 *
 * Fallback: If SDK hasn't initialized yet (e.g., on page refresh),
 * this function will read the DS cookie directly.
 *
 * Returns the token value if available, null otherwise
 */
export const getDescopeSessionToken = (): string | null => {
  try {
    // First, try to use Descope SDK's built-in method
    const sessionToken = getSessionToken();
    if (sessionToken) {
      return sessionToken;
    }
  } catch {
    // SDK not available or token access failed (e.g., server-side rendering)
  }

  // Fallback: Try to read DS cookie directly if SDK hasn't initialized yet
  // This is useful on page refresh when Descope SDK is still initializing
  try {
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      const dsCookie = cookies.find((cookie) =>
        cookie.trim().startsWith('DS='),
      );
      if (dsCookie) {
        const token = dsCookie.split('=')[1]?.trim();
        if (token) {
          return decodeURIComponent(token);
        }
      }
    }
  } catch {
    // Cookie access failed
  }

  return null;
};
